import type { ToolPart } from '../../types';

/**
 * Inline renderer for `python_run` tool calls.
 *
 * Renders the code the agent executed + its sandbox output, split into
 * stdout / stderr / error / note sections. The formatting contract comes
 * from `agent/tools.py::make_python_run`, which emits sections separated
 * by literal `--- stderr ---`, `--- error ---`, `--- note: ... ---`
 * markers (exactly one of each, in that order, any may be absent).
 *
 * Keeping the parser dumb: we look for those markers as whole lines and
 * slice the text. If the format ever changes we're at worst showing the
 * whole result as stdout, which is still legible.
 */

interface Props {
  part: ToolPart;
}

interface ParsedOutput {
  stdout: string;
  stderr: string;
  error: string;
  note: string;
}

const SECTION_MARKERS = [
  { key: 'stderr' as const, marker: '--- stderr ---' },
  { key: 'error' as const, marker: '--- error ---' },
  { key: 'note' as const, marker: '--- note:' },
];

function parseOutput(raw: string | undefined): ParsedOutput {
  const out: ParsedOutput = { stdout: '', stderr: '', error: '', note: '' };
  if (!raw) return out;

  // Find section boundaries by marker line, preserving order of appearance.
  const lines = raw.split('\n');
  const sections: { key: keyof ParsedOutput; start: number }[] = [
    { key: 'stdout', start: 0 },
  ];
  lines.forEach((line, idx) => {
    for (const { key, marker } of SECTION_MARKERS) {
      if (line.startsWith(marker)) {
        sections.push({ key, start: idx + 1 });
      }
    }
  });

  for (let i = 0; i < sections.length; i++) {
    const section = sections[i]!;
    const { key, start } = section;
    const nextSection = sections[i + 1];
    const end = nextSection ? nextSection.start - 1 : lines.length;
    const body = lines.slice(start, end).join('\n').replace(/\n+$/, '');
    if (key === 'note' && start - 1 >= 0) {
      out[key] = (lines[start - 1] ?? '').replace(/^---\s*note:\s*/, '').replace(/\s*---$/, '');
    } else {
      out[key] = body;
    }
  }
  return out;
}

function dotClass(status: string): string {
  if (status === 'completed') return 'tool-dot-done';
  if (status === 'error') return 'tool-dot-error';
  return 'tool-dot-active';
}

export function PythonRunCard({ part }: Props) {
  const isActive = part.state.status === 'running';
  const code = (part.state.input?.code as string | undefined) || '';
  const lineCount = code ? code.split('\n').length : 0;

  const parsed = parseOutput(part.state.output);
  const hasStdout = parsed.stdout.length > 0;
  const hasStderr = parsed.stderr.length > 0;
  const hasErr = parsed.error.length > 0 || !!part.state.error;
  const hasNote = parsed.note.length > 0;

  let label: string;
  if (isActive) {
    label = 'Запускаю Python…';
  } else if (hasErr) {
    label = `Python · ошибка${lineCount ? ` · ${lineCount} стр.` : ''}`;
  } else {
    label = `Python · выполнено${lineCount ? ` · ${lineCount} стр.` : ''}`;
  }

  const errorText = parsed.error || part.state.error || '';

  return (
    <div className={`tool-step tool-${part.state.status}`}>
      <div className="tool-step-rail">
        <div className={`tool-step-dot ${dotClass(part.state.status)}`} />
      </div>
      <div className="tool-step-body">
        <details>
          <summary className={`tool-step-label ${isActive ? 'tool-active' : ''}`}>
            {label}
          </summary>
          <div className="tool-step-content">
            {code && (
              <pre className="tool-command python-run-code">{code}</pre>
            )}
            {hasStdout && (
              <pre className="tool-output">{parsed.stdout}</pre>
            )}
            {hasStderr && (
              <pre className="tool-output python-run-stderr">{parsed.stderr}</pre>
            )}
            {hasErr && (
              <pre className="tool-error">{errorText}</pre>
            )}
            {hasNote && (
              <div className="python-run-note">{parsed.note}</div>
            )}
          </div>
        </details>
      </div>
    </div>
  );
}
